/* eslint-disable max-lines */
import { Injectable, Injector } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  ColDef,
  ColumnApi,
  GridApi,
  ValueFormatterParams,
} from 'ag-grid-community';
import {
  cloneDeep,
  countBy,
  filter,
  forEach,
  get,
  isEmpty,
  isEqual,
  isNil,
  map,
  reduce,
  set,
  some,
  sortBy,
  toLower,
  values,
} from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  InventoryItemVenue,
  Threshold,
} from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';

import {
  observableCombineLatest,
  observableOf,
  rx,
  Subscription,
} from '@shared/rx';
import { ValidationService } from '@components/form/services/validation/validation.service';
import {
  CreateFormRules,
  ENTITY_TYPES,
  FromAndToSiteId,
  GRID_LIST_VIEW_TYPE,
  GridRowData,
  IBaseEntityResponse,
  IBaseEntityType,
  IBaseFormService,
  IEvent,
  ITransferFormFieldPath,
  MEASURE_TRANSACTION,
  ObjectId,
  TRANSFER_TYPE,
  ValueFormatterFn,
  XuxGridAction,
  XuxGridActionPayload,
} from '@shared/types';
import { lazyInject } from '@containers/base/decorators';
import { TransferTotalFormService } from '@shared/services/transfer/transfer-totals-form.service';
import { TransferFormService } from '@shared/services/transfer/transfer.form.service';
import { InventoryItemVenueService } from '@app/shared/services/inventory-item-venue';
import {
  flatTreeDataByHasChildren,
  generateUnitTextV2,
  getDefaultMeasure,
} from '@shared/utils';
import {
  getFormControl,
  getFormFieldValue,
  setFormFieldValue,
} from '@components/form/services/schema-form/schema-form-functions';
import { ITreeData } from '@shared/types/common/tree.type';
import { ENTITY_ID_PATH } from '@config/global-constants';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import {
  IGridInventoryItemVenueTotalFormCallBack,
  InitialFactorAndCountValue,
  IPageJumpRouteState,
  IQohInventoryItemVenue,
  IQuantityOnHandResponse,
  ITransfer,
  SelectedSite,
  TooltipIconConfig,
} from '@shared/types/entities';
import { DecimalNumber } from '@app/shared/common';
import { transferModalDefinition } from '@shared/services/transfer/modal.definition';
import { TransferAutoRestockService } from '@shared/services/transfer-auto-restock';

import { QohService } from '../qoh';
import { createFormRules as createTransferDestinationsFormRules } from './form-rules';
import {
  appendInitialFactorAndCountValue,
  createPendingQuantityMap,
  getCurrentItemQoh,
  getItemThreshold,
  getItemThresholdConfig,
  getItemThresholdWarning,
  handleTransferUnitChange,
  initInventoryThresholdMapper,
  getParQty,
  qohResultOfDivFactorValue,
  updateInitialFactorAndCountValue,
} from '../transfer/transfer.util';
import { StandWorksheetCalcServiceProvider } from '../stand-worksheet/calculation-services/stand-worksheet-calc-service-provider';
import { TransferBaseFormService } from '@shared/services/transfer-common';
import { select } from '@angular-redux/store';
import { STATE_KEYS } from '../state-service/state-selectors';
import { ScanningService } from '../scanning';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class TransferDestinationsFormService
  extends TransferBaseFormService
  implements IBaseFormService<ENTITY_TYPES.TRANSFER_DESTINATIONS>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.TRANSFER;

  public get gridListView(): GRID_LIST_VIEW_TYPE {
    return this.stateService.getTransferItemListViewSetting();
  }

  @select(STATE_KEYS.UI_EDITOR_OPTIONS_STATE)
  public routeState$: Observable<IPageJumpRouteState>;

  @lazyInject(TransferFormService)
  public transferFormService: TransferFormService;
  @lazyInject(ValidationService) public validationService: ValidationService;
  @lazyInject(TransferTotalFormService)
  public totalFormService: TransferTotalFormService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(TransferAutoRestockService)
  public transferAutoRestockService: TransferAutoRestockService;
  @lazyInject(QohService) public qohService: QohService;
  @lazyInject(StandWorksheetCalcServiceProvider)
  public standWorksheetCalcServiceProvider: StandWorksheetCalcServiceProvider;
  @lazyInject(ScanningService) public scanningService: ScanningService;

  protected subscriptions: Subscription[] = [];

  public listGridApi: GridApi;
  public form: FormGroup;
  public selectedEvent: IEvent;
  public transferType: TRANSFER_TYPE;
  public siteId: ObjectId;
  public siteIdTo: ObjectId;
  public originFrom: string;
  public createTime: string;
  public createFormRulesFunction: CreateFormRules<TransferDestinationsFormService> =
    createTransferDestinationsFormRules;
  public formFieldPath: ITransferFormFieldPath = {
    isChargeable: 'is_chargeable',
    transferType: 'transfer_type',
    siteId: 'site_id',
    siteIdTo: 'site_id_to',
    inventoryItemVenues: 'inventory_items',
    measureEntityId: 'measure_entity_id',
    measureFactorValue: 'measure_factor_value',
    initialCount: 'initial_count',
    count: 'count',
    departmentCodeTo: 'site_department_code_to',
    totalCost: 'total_cost',
    category: 'category',
    quantityReversed: 'quantity_reversed',
    reversedCost: 'reversed_cost',
    receivedTotalCost: 'received_total_cost',
    glAccountName: 'gl_account.name',
    virtualTotals: 'virtual.totals',
    virtualTransferDestinations: 'virtual.transfer_destinations',
    virtualSelectedEvent: 'virtual.selected_event',
    qohFrom: 'qoh_from',
  };

  public isShownSubmittedColumns$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  public constructor(public injector: Injector) {
    super(injector);
  }

  public onFormReady(form: FormGroup): void {
    this.form = form;
    this.initItemListSubscription();
    this.initKeyDownEventSubscription();
  }

  public initDataFromRouteState(
    selectedSite: SelectedSite,
    storeForm: ITransfer,
    originFrom?: string,
    createTime?: string,
  ): void {
    this.selectedEvent = get(
      storeForm,
      this.formFieldPath.virtualSelectedEvent,
    );
    this.transferType = get(storeForm, this.formFieldPath.transferType);
    this.siteId = get(storeForm, this.formFieldPath.siteId);
    this.siteIdTo = get(selectedSite, 'id');
    this.originFrom = originFrom;
    this.createTime = createTime;
    const departmentCode =
      this.transferFormService.retrieveDepartmentCode(selectedSite);
    setFormFieldValue(
      this.form,
      this.formFieldPath.departmentCodeTo,
      departmentCode,
      false,
    );
  }

  public initKeyDownEventSubscription(): void {
    this.scanningService.scanOptions = {
      isCategoryListView: this.isCategoryListView(),
      focusKeyForListModal: this.formFieldPath.initialCount,
      focusKeyForTreeModal: this.formFieldPath.initialCount,
      isCompleted: this.isFinalStatusFun(),
      focusOnNextRow: false,
      customEntityIdPath: 'item_entity_id',
    };
    const scanningSubscription =
      this.scanningService.returnScanningSubscription(
        this.scanningService.scanOptions,
      );
    this.subscriptions.push(scanningSubscription);
  }

  public destroyFormDefinition(): void {
    this.inventoryItemThresholdMapper = null;
    this.hiddenThresholdInfo$.next(true);
    this.viewSettingToggleValue = false;
    this.scanningService.destroyServiceDefinition();
    super.destroyFormDefinition();
  }

  public getInventoryItemVenuesRequireValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlRoot = control.root;
      const controlParent = control.parent;

      if (isNil(controlRoot.value) || isNil(controlParent)) return null;

      const data = flatTreeDataByHasChildren(control.value);
      const hasQuantityItems = some(data, (item) => {
        const castNumber = new DecimalNumber(
          item[this.formFieldPath.initialCount],
        );

        return castNumber.gt(0) || castNumber.lt(0);
      });

      return hasQuantityItems
        ? null
        : { error: I18n.ErrorMessages.TransferNoQuantityItemsError };
    };
  }

  /**
   * Initializing Totals Calculation
   *
   * @param callback Totals data update callback
   */
  public initItemTotalsSubscription(
    callback: IGridInventoryItemVenueTotalFormCallBack,
  ): void {
    const subscription = this.totalFormService.handleTotalsSubscription(
      this,
      callback,
    );
    this.subscriptions.push(subscription);
  }

  public initItemListSubscription(): void {
    const subscription = observableCombineLatest([
      this.fieldValueChanges(this.formFieldPath.inventoryItemVenues),
      this.isQohDataFetching$,
      this.isPendingQuantityFetching$,
    ])
      .pipe(rx.distinctUntilChanged(isEqual))
      .subscribe(
        ([items, isQohDataFetching, isPendingQuantityFetching]: [
          IQohInventoryItemVenue[],
          boolean,
          boolean,
        ]): void => {
          const inventoryItemFormValue = isEmpty(items)
            ? getFormFieldValue(
                this.form,
                this.formFieldPath.inventoryItemVenues,
              )
            : items;
          this.replenishMessageHidden$.next(true);
          this.replenishButtonDisabled$.next(
            (isEmpty(inventoryItemFormValue) &&
              isEmpty(this.allInventoryItems)) ||
              isQohDataFetching ||
              isPendingQuantityFetching,
          );
          this.clearQtyRequestBtnDisable$.next(
            isEmpty(items) || !this.enableClearQtyRequestedBtn(),
          );
          this.clearQtyGroupBtnDisable$.next(
            isEmpty(items) || !this.enableClearQtyBtnGroup(),
          );
        },
      );
    this.subscriptions.push(subscription);
  }

  public updateInventoryItemVenues(
    items: IQohInventoryItemVenue[],
    markField: boolean = false,
    saveUpdatedItems: boolean = false,
  ): void {
    const updatedInventoryItems: IQohInventoryItemVenue[] = map(
      items,
      (item) => ({
        ...item,
        initialFactorAndCountValue: updateInitialFactorAndCountValue(
          item as Partial<IQohInventoryItemVenue>,
        ),
      }),
    );
    const treeData = this.convertInventoryItemVenuesToTree(
      updatedInventoryItems,
    );

    this.updateItemsByCompanyPreferences(
      treeData,
      this.formFieldPath.inventoryItemVenues,
      markField,
    );
    getFormControl(
      this.form,
      this.formFieldPath.inventoryItemVenues,
    )?.updateValueAndValidity({
      onlySelf: true,
      emitEvent: true,
    });
    this.preserveOrUpdateAllInventoryItems(
      saveUpdatedItems,
      updatedInventoryItems,
    );

    this.isInventoryItemVenuesGridBusy$.next(false);
    setTimeout(() => {
      this.listGridApi.expandAll();
    });
  }

  public isTransferByType(
    condition: TRANSFER_TYPE,
    typeValue?: TRANSFER_TYPE,
  ): boolean {
    const transferTypeValue: TRANSFER_TYPE = typeValue ?? this.transferType;

    return isEqual(condition, transferTypeValue);
  }

  public isOutsideOfEventTransfer(): boolean {
    return this.isTransferByType(TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER);
  }

  public getQuantityCellClassRules(): ColDef['cellClassRules'] {
    return {
      'ag-numeric-cell': (): boolean => true,
      'show-placeholder': ({ value }): boolean => {
        // shown placeholder when can edit it
        return !(value || value === 0);
      },
    };
  }

  /**
   * @deprecated should use takeIntersectionAndProcessThreshold in the replenish utils instead
   */
  public takeIntersectionAndProcessThreshold(
    res: IBaseEntityResponse,
    siteIdTo: string,
  ): IQohInventoryItemVenue[] {
    const entityIdDictionary = countBy(res.items, 'entity_id');

    return filter(res.items, (item: IQohInventoryItemVenue) => {
      return (
        entityIdDictionary[item.entity_id] > 1 &&
        isEqual(item.site_id, siteIdTo)
      );
    }) as IQohInventoryItemVenue[];
  }

  public getQohValueGetter(type: string): ValueFormatterFn {
    const getQohValueGetterFn: ValueFormatterFn = (
      params: ValueFormatterParams,
    ): string => {
      if (!params.data.isLeaf) return null;

      return qohResultOfDivFactorValue(params, type, this.numberFormatService);
    };

    return getQohValueGetterFn;
  }

  /**
   * TODO: need simplify this function with transfer.services.ts.
   *
   * @param path
   * @returns
   */
  public getIconsInfoRendererParams(): Record<
    string,
    string | boolean | object
  > {
    return {
      isBusy: observableCombineLatest([
        this.isQohDataFetching$,
        this.isPendingQuantityFetching$,
      ]).pipe(
        rx.map(
          ([isQohDataFetching, isPendingQuantityFetching]) =>
            isQohDataFetching || isPendingQuantityFetching,
        ),
      ),
      dataSource: observableCombineLatest([
        this.qohData$,
        this.pendingQuantityData$,
      ]),
      valueMapper: ({ rowData, cellValue, params }): TooltipIconConfig[] => {
        const result: TooltipIconConfig[] = [];
        const newErrorValue: string[] = [];

        if (!rowData.isLeaf || !cellValue) {
          params.node.setDataValue('error', newErrorValue);

          return result;
        }

        const thresholdWarning = getItemThresholdWarning({
          cellValue,
          rowData,
          numberFormatter: this.numberFormatService.getNumberFormatter(false),
          i18nService: this.i18nService,
        });
        if (!isNil(thresholdWarning)) {
          result.push(thresholdWarning);
          newErrorValue.push('quantityRequestThresholdWarning');
        }

        params.node.setDataValue('error', newErrorValue);
        if (
          this.inventoryItemThresholdMapper[rowData.entity_id].error !==
          newErrorValue.includes('quantityRequestThresholdWarning')
        ) {
          this.inventoryItemThresholdMapper[rowData.entity_id].error =
            newErrorValue.includes('quantityRequestThresholdWarning');
          this.setThresholdInfoMessage();
        }

        return result;
      },
    };
  }

  public setThresholdInfoMessage(): void {
    const needThresholdWarningItems = filter(
      this.inventoryItemThresholdMapper,
      (item) => item.error,
    );

    this.hiddenThresholdInfo$.next(
      isEqual(values(needThresholdWarningItems).length, 0),
    );
    if (values(needThresholdWarningItems).length > 0) {
      setTimeout(() => {
        this.thresholdInfoTitle$.next(
          this.i18nService.translate(
            I18n.InfoMessages.ThresholdWarningMessage,
            {
              count: values(needThresholdWarningItems).length,
            },
          ),
        );
      });
    }
  }

  public fetchInventoryItemVenues([
    siteId,
    siteIdTo,
  ]: FromAndToSiteId): Observable<IBaseEntityResponse> {
    const query = {
      filter: 'measures/transactions/transfer eq in(true)',
      select: [
        'is_chargeable',
        'code',
        'reporting_category_major',
        'reporting_category_minor',
        'name',
        'gl_account',
        'measures',
        'vendors',
        'preferred_vendor',
        'thresholds',
        'costs',
        'upc_codes',
      ],
    };

    return this.inventoryItemVenueService.fetchCurrentList(query, [
      siteId,
      siteIdTo,
    ]);
  }

  public async getInventoryItemVenues(
    [siteId, siteIdTo]: FromAndToSiteId,
    inventoryItems: ITreeData[],
  ): Promise<void> {
    const inventoryItemVenueMap = reduce(
      inventoryItems,
      (resultMap, item) => {
        const hasInitialCount = get(item, 'initial_count', null);

        if (isNil(hasInitialCount)) {
          return resultMap;
        }

        return {
          ...resultMap,
          [item.entity_id]: {
            [this.formFieldPath.initialCount]: item.initial_count,
            [this.formFieldPath.measureEntityId]: item.measure_entity_id,
          },
        };
      },
      {},
    );

    this.isInventoryItemVenuesGridBusy$.next(true);
    this.fetchInventoryItemVenues([siteId, siteIdTo])
      .pipe(
        rx.map((res: IBaseEntityResponse) => {
          return this.processInventoryItems(res);
        }),
        rx.tap(
          (res) =>
            (this.inventoryItemThresholdMapper = initInventoryThresholdMapper(
              res.items as IQohInventoryItemVenue[],
            )),
        ),
        rx.map((res: IBaseEntityResponse<InventoryItemVenue>) => {
          const inventoryItemVenues = this.takeIntersectionAndProcessThreshold(
            res,
            siteIdTo,
          );

          return <IBaseEntityResponse<InventoryItemVenue>>{
            ...res,
            items: inventoryItemVenues,
            total: inventoryItemVenues.length,
          };
        }),
        rx.map((res: IBaseEntityResponse) =>
          this.mixinAlreadyExistentData(
            res.items as InventoryItemVenue[],
            inventoryItemVenueMap,
          ),
        ),
        rx.mergeMap((res: IQohInventoryItemVenue[]) =>
          this.getQuantityOnHandData(res, siteId, siteIdTo).pipe(
            rx.map(() => res),
          ),
        ),
        rx.mergeMap((res: IQohInventoryItemVenue[]) =>
          this.getPendingQuantity(siteId, siteIdTo).pipe(rx.map(() => res)),
        ),
        rx.map((res: IQohInventoryItemVenue[]) => this.addQOHForItems(res)),
        rx.map((res: IQohInventoryItemVenue[]) =>
          appendInitialFactorAndCountValue(res),
        ),
        rx.map((res) => this.processParQty(res)),
        rx.tap(() => this.setThresholdInfoMessage()),
      )
      .subscribe((items: IQohInventoryItemVenue[]) => {
        this.updateInventoryItemVenues(items, false, true);
      });
  }

  public mixinAlreadyExistentData(
    inventoryItemVenues: InventoryItemVenue[],
    inventoryItemVenueMap: IBaseEntityType = {},
  ): IQohInventoryItemVenue[] {
    if (isEmpty(inventoryItemVenues)) {
      return [];
    }

    if (isEmpty(inventoryItemVenueMap)) {
      return inventoryItemVenues as IQohInventoryItemVenue[];
    }

    return map(inventoryItemVenues, (item): IQohInventoryItemVenue => {
      const hasCurrentData = get(inventoryItemVenueMap, item.entity_id, null);

      if (isNil(hasCurrentData)) {
        return item as IQohInventoryItemVenue;
      }

      return {
        ...item,
        [this.formFieldPath.initialCount]: get(inventoryItemVenueMap, [
          item.entity_id,
          this.formFieldPath.initialCount,
        ]),
        [this.formFieldPath.measureEntityId]: get(inventoryItemVenueMap, [
          item.entity_id,
          this.formFieldPath.measureEntityId,
        ]),
      } as IQohInventoryItemVenue;
    });
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   *
   * in auto restock page, if click the replenish button, in fact the this.selectEvent is still undefined
   * so will use the selectedEvent from auto-restock page
   */
  public processInventoryItems(
    res: IBaseEntityResponse,
    selectedEvent: IEvent = this.selectedEvent,
    isOutsideOfEventTransfer: Function = this.isOutsideOfEventTransfer.bind(
      this,
    ),
  ): IBaseEntityResponse {
    forEach(res.items, (inventoryItem: IQohInventoryItemVenue) => {
      const measures =
        (isNil(inventoryItem.origin_measures)
          ? inventoryItem.measures
          : inventoryItem.origin_measures) || [];
      const transferMeasures = filter(
        measures,
        (item) => item.transactions.transfer,
      );
      // sort the transfer Unit by description
      const sortMeasures = sortBy(
        map(transferMeasures, (filterItem) => {
          return {
            ...filterItem,
            text: generateUnitTextV2(filterItem, {
              numberFormatService: this.numberFormatService,
              measureTransactionType: MEASURE_TRANSACTION.Transfer,
            }),
            value: filterItem.entity_id,
            description: filterItem.description,
          };
        }),
        (item) => toLower(item.description),
      );

      set(inventoryItem, 'origin_measures', measures);
      set(inventoryItem, 'measures', sortMeasures);
      set(inventoryItem, 'error', []);

      const itemThreshold: Threshold =
        getItemThreshold(
          inventoryItem,
          isOutsideOfEventTransfer(),
          selectedEvent,
        ) || null;
      const iconsInfo = getItemThresholdConfig(
        itemThreshold,
        inventoryItem.origin_measures,
      );
      set(inventoryItem, 'iconsInfo', iconsInfo);
      // if the document have the measureEntityId use it else use first value
      inventoryItem[this.formFieldPath.measureEntityId] = get(
        getDefaultMeasure(sortMeasures, MEASURE_TRANSACTION.Transfer),
        ENTITY_ID_PATH,
      );
      inventoryItem[this.formFieldPath.initialCount] = undefined;

      const parQty = getParQty(
        inventoryItem,
        selectedEvent,
        isOutsideOfEventTransfer(),
      );
      set(inventoryItem, 'par_qty', parQty);
    });

    return res;
  }

  public addQOHForItems(
    inventoryItems: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    return map(inventoryItems, (item) => {
      const qohTo = this.getQohAndPendingQuantity(
        item,
        this.formFieldPath.siteIdTo,
      );
      const qohFrom = this.getQohAndPendingQuantity(
        item,
        this.formFieldPath.siteId,
      );

      return { ...item, qohTo, qohFrom };
    });
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getQohAndPendingQuantity(
    item: IQohInventoryItemVenue,
    path: string,
  ): string[] {
    const qohDataForCurrentRow = getCurrentItemQoh(
      this.qohData,
      isEqual(this.formFieldPath.siteId, path) ? this.siteId : this.siteIdTo,
      item,
    );
    const pendingQtyForCurrentRow = get(
      this.pendingQuantityData,
      item.entity_id,
    );

    return [
      get(qohDataForCurrentRow, this.formFieldPath.count, 0)?.toString(),
      get(pendingQtyForCurrentRow, path, 0)?.toString(),
    ];
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getQuantityOnHandData(
    inventoryItemVenues: IQohInventoryItemVenue[],
    siteId: string,
    siteIdTo: string,
  ): Observable<
    | IBaseEntityType
    | IQuantityOnHandResponse
    | IBaseEntityResponse<IBaseEntityType>
  > {
    if (isEmpty(inventoryItemVenues)) {
      return observableOf(null);
    }
    this.isQohDataFetching$.next(true);

    return this.transferService
      .getQuantityOnHandData(
        inventoryItemVenues,
        siteId,
        siteIdTo,
        ENTITY_ID_PATH,
      )
      .pipe(
        rx.catchError(this.catchHttpError),
        rx.tap((res) => {
          this.qohData = res.items;
          this.qohData$.next(res.items);
          this.isQohDataFetching$.next(false);
        }),
      );
  }

  public fetchPendingQuantity(
    siteId: string,
    siteIdTo: string,
  ): Observable<IBaseEntityResponse> {
    return this.transferService
      .fetchList(
        {
          filter: `status eq 'pending' and ((site_id eq in(${siteId},${siteIdTo})) or (site_id_to eq in(${siteId},${siteIdTo})))`,
          select: [
            'site_id',
            'site_id_to',
            'inventory_items.item_entity_id',
            'inventory_items.initial_count',
            'inventory_items.measure_factor_value',
          ],
        },
        {
          skipMappers: true,
        },
        true,
      )
      .pipe(rx.catchError(this.catchHttpError));
  }

  public getPendingQuantity(
    siteId: string,
    siteIdTo: string,
  ): Observable<IBaseEntityResponse<IBaseEntityType>> {
    this.isPendingQuantityFetching$.next(true);

    return this.fetchPendingQuantity(siteId, siteIdTo).pipe(
      rx.tap((res) => {
        const pendingQtyMap = createPendingQuantityMap(
          this,
          res,
          siteId,
          siteIdTo,
        );
        this.pendingQuantityData = pendingQtyMap;
        this.pendingQuantityData$.next(pendingQtyMap);
        this.isPendingQuantityFetching$.next(false);
      }),
    );
  }

  public getGridReadyHandler(): (
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ) => void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return (action: XuxGridAction, grid: XuxEditGridComponent): void => {
      this.listGridApi = action.payload?.gridApi;
      this.scanningService.gridApi = this.listGridApi;
      this.scanningService.columnApi =
        action?.payload?.gridApi?.['columnController']?.columnApi;
      this.hiddenOrDisplayQohFromColumn(action);
      if (!this.isFinalStatusFun()) {
        this.showTransferUnitTooltips(
          action.payload?.gridApi,
          this.formFieldPath.measureEntityId,
        );
      }
      action.payload?.gridApi.expandAll();
    };
  }

  public hiddenOrDisplayQohFromColumn(action: XuxGridAction): void {
    const columnApi: ColumnApi =
      action.payload?.gridApi['columnController'].columnApi;

    if (!this.transferService.getDisplayQohFrom(this.siteId)) {
      columnApi.setColumnsVisible([this.formFieldPath.qohFrom], false);
      this.listGridApi?.sizeColumnsToFit();
    }
  }

  public getInlineChangeMapper(): (
    value: GridRowData,
    payload: XuxGridActionPayload,
  ) => GridRowData {
    return (value: GridRowData, payload: XuxGridActionPayload): GridRowData => {
      let newGridRowData: GridRowData;
      switch (payload.field) {
        case this.formFieldPath.measureEntityId:
          newGridRowData = handleTransferUnitChange(
            value,
            this.selectedEvent,
            this.isOutsideOfEventTransfer(),
          );
          this.updateSavedInventoryItems(newGridRowData);
          break;
        case this.formFieldPath.initialCount:
          const initialFactorAndCountValue: InitialFactorAndCountValue =
            updateInitialFactorAndCountValue(value);

          newGridRowData = {
            ...value,
            initialFactorAndCountValue,
          };
          this.updateSavedInventoryItems(newGridRowData);
          break;
        default:
          newGridRowData = value;
          break;
      }

      return newGridRowData;
    };
  }

  public preserveOrUpdateAllInventoryItems(
    saveUpdatedItems: boolean,
    inventoryItems: IQohInventoryItemVenue[],
  ): void {
    if (saveUpdatedItems) {
      this.allInventoryItems = cloneDeep(inventoryItems);
      if (this.viewSettingToggleValue) {
        const filteredItems = this.filterBlankRows(this.allInventoryItems);
        this.updateInventoryItemVenues(filteredItems);
      }
    } else {
      forEach(inventoryItems, (item) => this.updateSavedInventoryItems(item));
    }
  }

  public filterBlankRows(
    items: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    return filter(
      items,
      (item) => !isNil(item[this.formFieldPath.initialCount]),
    );
  }

  public updateSavedInventoryItems(
    updateItem: IQohInventoryItemVenue | GridRowData,
  ): void {
    this.allInventoryItems = map(this.allInventoryItems, (item) => {
      if (isEqual(item.entity_id, updateItem.entity_id)) {
        return { ...item, ...updateItem };
      }

      return item;
    });
  }

  public isListView(): boolean {
    return isEqual(
      this.gridListView,
      GRID_LIST_VIEW_TYPE.ALPHABETICAL_LIST_VIEW,
    );
  }

  public async openViewSettingModal(): Promise<void> {
    const modalOptions = transferModalDefinition.viewSettingModal(
      this.viewSettingToggleValue,
    );
    const modalResult = await this.uiActions.promptFormModal(modalOptions);
    if (!modalResult.confirmed) return;
    this.viewSettingToggleValue = get(modalResult, 'value.hide_empty_rows');
    const currentItems = getFormFieldValue(
      this.form,
      this.formFieldPath.inventoryItemVenues,
    );
    if (this.viewSettingToggleValue) {
      let filteredItems;
      let flattenItems = currentItems;
      if (this.isListView()) {
        filteredItems = this.filterBlankRows(flattenItems);
      } else {
        flattenItems = flatTreeDataByHasChildren(currentItems);
        filteredItems = this.filterBlankRows(flattenItems);
      }
      this.updateInventoryItemVenues(filteredItems as IQohInventoryItemVenue[]);
    } else {
      let mergedItems = cloneDeep(this.allInventoryItems);
      if (this.isListView()) {
        mergedItems = sortBy(mergedItems, (item) => toLower(item?.name));
      }
      this.updateInventoryItemVenues(mergedItems);
    }
  }
}
// eslint-disable-next-line max-lines
